import PropTypes from 'prop-types';
import events from 'lib/events';
import ToolTip, {tooltipPositions} from 'components/tooltip';
import {systemTypeNames} from 'helpers/constants';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import React from 'react';
import EditableSelectField from 'components/forms/editableSelectField';
import LayoutCheckbox from 'components/forms/layoutCheckbox';
import LayoutSelect from 'components/forms/layoutSelect';
import LayoutInput from 'components/forms/layoutInput';
import SystemDetails from '../systemDetails.jsx';
import VolumeCalculator from 'helpers/volumeCalculator';
import {balanceColumns} from 'helpers/fieldHelpers';
import {systemOptionsTags} from 'helpers/dataHelpers';
import {chemicalBaseTypeNames, chemicalBaseTypes, systemTypes} from 'helpers/constants';
import {inSoftSoakTrioRange} from 'helpers/regionalDifferences';

class DetailsSection extends React.Component {
  static defaultProps = {
    condition: true,
  };

  static propTypes = {
    condition: PropTypes.bool.isRequired,
    dataChanged: PropTypes.func.isRequired,
    deleteEquipment: PropTypes.func,
    detailsExpanded: PropTypes.bool.isRequired,
    edit: PropTypes.bool.isRequired,
    equipment: PropTypes.array,
    equipmentChanged: PropTypes.func,
    equipmentTypes: PropTypes.array,
    newEquipment: PropTypes.func,
    pool: PropTypes.object.isRequired,
    productDict: PropTypes.object.isRequired,
    systemID: PropTypes.string.isRequired,
    toggleSystemDetails: PropTypes.func,
    dealer: PropTypes.object.isRequired,
    group: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
  };

  componentWillReceiveProps = nextProps => {
    if (nextProps.pool.volume === this.props.pool.volume) {
      return;
    }

    if (nextProps.pool.softSoakTRIO && nextProps.pool.volume && !inSoftSoakTrioRange(nextProps.dealer, nextProps.pool)) {
      this.props.dataChanged({key: 'softSoakTRIO', value: false});
    }
  };

  addEquipment = () => {
    let type = parseInt(this.equipTypeSelect.value);

    return this.props.newEquipment(type);
  };

  notifyChangeSystemType = x => {
    let {formatMessage} = this.props.intl;

    if (this.props.pool && this.props.pool.id) { // Don't notify on new pool creation
      events.publish('warn.user',
        {key: 'products.changed',
          message: formatMessage({id: 'system.details.some.products'}) + ` ${systemTypeNames[x.value]} ` + formatMessage({id: 'system.details.have.changed'})});
    }
    this.props.dataChanged(x);
  };

  volumeChanged = e => {
    if (e.key === 'volume' && e.value) {
      this.props.dataChanged(e);
    }
  };

  renderSupplemental = pool => {
    let supplemental = [];
    let softswimpool = false;
    let {formatMessage} = this.props.intl;
    let productDict = this.props.productDict;

    if (productDict.sanitizer.chemicalBaseType === chemicalBaseTypes.softswimPool) {
      softswimpool = true;
    }

    supplemental.push(<LayoutCheckbox editMode={this.props.edit}
                                      label={formatMessage({id: 'system.details.ionization.catalyst'})}
                                      dataKey='ionization'
                                      key='ionization'
                                      disabled={softswimpool}
                                      value={pool.ionization}
                                      dataChanged={this.props.dataChanged}/>);
    supplemental.push(<LayoutCheckbox editMode={this.props.edit}
                                      label={formatMessage({id: 'general.ozonator'})}
                                      dataKey='ozonator'
                                      key='ozonator'
                                      disabled={softswimpool}
                                      value={pool.ozonator}
                                      dataChanged={this.props.dataChanged}/>);

    return <div>
      <h2 className='with-keyline'><FormattedMessage id="general.supplemental"/></h2>
      {balanceColumns(supplemental, 3)}
    </div>;
  };

  renderSystemName = pool => {
    let {formatMessage} = this.props.intl;
    let systemName = pool.softSoakTRIO ? 'Trio ' : '';

    systemName += chemicalBaseTypeNames(pool.typeID, pool.chemicalBaseTypeID || 2, formatMessage);

    let nameFields = [];

    nameFields.push(<LayoutInput editMode={this.props.edit}
                                 label={formatMessage({id: 'system.details.system.nickname'})}
                                 dataKey='nickName'
                                 grayedOut={!pool.nickName && !this.props.edit}
                                 value={!pool.nickName && !this.props.edit ? formatMessage({id: 'system.widget.add.nickname'}) : pool.nickName}
                                 placeholder={formatMessage({id: 'system.details.system.nickname'})}
                                 dataChanged={this.props.dataChanged}
                                 maxLength='50'/>);
    nameFields.push(<LayoutInput editMode={false}
                                 label={formatMessage({id: 'system.details.system.name'})}
                                 value={systemName}/>);

    return <div>
      <h2 className='with-keyline'><FormattedMessage id="system.details.system.name"/></h2>
      {balanceColumns(nameFields, 3)}
    </div>;
  };

  renderWaterFeatures = pool => {
    let waterFeatures = [];
    let {formatMessage} = this.props.intl;

    waterFeatures.push(<LayoutCheckbox editMode={this.props.edit}
                                       label={formatMessage({id: 'system.details.attached.spa'})}
                                       dataKey='attachedSpa'
                                       key='attachedSpa'
                                       value={pool.attachedSpa}
                                       dataChanged={this.props.dataChanged}/>);
    waterFeatures.push(<LayoutCheckbox editMode={this.props.edit}
                                       label={formatMessage({id: 'system.details.water.fountain'})}
                                       dataKey='waterFountain'
                                       key='waterFountain'
                                       value={pool.waterFountain}
                                       dataChanged={this.props.dataChanged}/>);
    waterFeatures.push(<LayoutCheckbox editMode={this.props.edit}
                                       label={formatMessage({id: 'general.waterfall'})}
                                       dataKey='waterfall'
                                       key='waterfall'
                                       value={pool.waterfall}
                                       dataChanged={this.props.dataChanged}/>);
    waterFeatures.push(<LayoutCheckbox editMode={this.props.edit}
                                       label={formatMessage({id: 'system.details.infloor.cleaning'})}
                                       dataKey='infloorCleaner'
                                       key='infloorCleaner'
                                       value={pool.infloorCleaner}
                                       dataChanged={this.props.dataChanged}/>);
    waterFeatures.push(<LayoutCheckbox editMode={this.props.edit}
                                       label={formatMessage({id: 'system.details.jetted.returns'})}
                                       dataKey='jettedReturns'
                                       key='jettedReturns'
                                       value={pool.jettedReturns}
                                       disabled={pool.typeID === systemTypes.swimspa}
                                       dataChanged={this.props.dataChanged}/>);

    if (pool.typeID === systemTypes.spa) {
      return null;
    }

    return <div>
      <h2 className='with-keyline'><FormattedMessage id="system.details.water.features"/></h2>
      {balanceColumns(waterFeatures, 3)}
    </div>;
  };

  render() {
    let {formatMessage} = this.props.intl;
    let pool = this.props.pool;
    let dealer = this.props.dealer;

    pool.typeID = parseInt(pool.typeID);

    let systemDetailsList = [];

    if (this.props.equipment) {
      for (let i = 0; i < this.props.equipment.length; ++i) {
        let e = this.props.equipment[i];

        systemDetailsList.push(
          <div className='system-details'>
            <SystemDetails editMode={this.props.edit}
                           equipment={e}
                           types={this.props.equipmentTypes}
                           onChange={this.props.equipmentChanged(e, i)}
                           onDelete={this.props.deleteEquipment(e, i)}
                           dataKey={i.toString()}/>
          </div>
        );
      }

      if (!systemDetailsList.length) {
        systemDetailsList = <p className='col-xs-12'><FormattedMessage id="system.details.additional.details"/></p>;
      }
    }

    let details = null;

    if (this.props.detailsExpanded) {
      details =
        <div>
          <div className='system-details-rows clearfix'>
            {systemDetailsList}
          </div>
          {this.props.edit ?
            <div>
              <button className='btn btn-primary'
                      onClick={this.addEquipment}>
                <i className='bl bl-add'/> <FormattedMessage id="system.details.add.detail"/>
              </button>
              <div className='col-xs-4'>
                <select className='form-control'
                        ref={e => this.equipTypeSelect = e}>
                  {this.props.equipmentTypes.map(et => <option value={et.id}
                                                               key={et.id}>{et.name}</option>)}
                </select>
              </div>
            </div> :
            null}
          <p>&nbsp;</p>
        </div>;
    }

    const simpleFormat = id => formatMessage({id});

    let filters = systemOptionsTags[pool.typeID].filterMedia;

    if (this.props.productDict.sanitizer.chemicalBaseType === chemicalBaseTypes.softswimPool) {
      filters['EcoKlean'] = 'general.filterMeda.ecoklean';
    }

    let fields = [];

    fields.push(
      <div className='form-group'>
        <label><FormattedMessage id="general.volume"/><span className='error'
                                                            style={{marginLeft: 2}}>*</span></label>
        <VolumeCalculator
          dataKey='volume'
          editMode={this.props.edit}
          group={this.props.group}
          onChange={this.volumeChanged}
          poolType={pool.typeID}
          region={dealer.country}
          title={formatMessage({id: 'system.details.select.calculate'})}
          volume={pool.volume}
        />
      </div>
    );

    fields.push(<LayoutSelect editMode={this.props.edit}
                              label={formatMessage({id: 'general.location'})}
                              dataKey='location'
                              key='location'
                              value={pool.location}
                              dataChanged={this.props.dataChanged}
                              options={Object.values(systemOptionsTags[pool.typeID].location).map(simpleFormat)}
                              keys={Object.keys(systemOptionsTags[pool.typeID].location)}/>);

    fields.push(<LayoutSelect editMode={this.props.edit}
                              label={formatMessage({id: 'general.finish'})}
                              dataKey='finish'
                              key='finish'
                              value={pool.finish}
                              dataChanged={this.props.dataChanged}
                              options={Object.values(systemOptionsTags[pool.typeID].finish).map(simpleFormat)}
                              keys={Object.keys(systemOptionsTags[pool.typeID].finish)}/>);
    fields.push(<LayoutSelect editMode={this.props.edit}
                              label={formatMessage({id: 'general.ground.level'})}
                              dataKey='groundLevel'
                              key='groundLevel'
                              value={pool.groundLevel}
                              dataChanged={this.props.dataChanged}
                              options={Object.values(systemOptionsTags[pool.typeID].groundLevel).map(simpleFormat)}
                              keys={Object.keys(systemOptionsTags[pool.typeID].groundLevel)}
                              tooltip={formatMessage(
                                {
                                  id: 'system.ground-level.splasher.tooltip',
                                },
                                {
                                  volume: dealer.country === 'CA' ? '9500 L' : '5000 gal',
                                })}
                              condition={pool.typeID !== systemTypes.spa}/>);
    fields.push(<LayoutSelect editMode={this.props.edit}
                              label={formatMessage({id: 'general.installation.type'})}
                              dataKey='property'
                              key='property'
                              value={pool.property}
                              dataChanged={this.props.dataChanged}
                              options={Object.values(systemOptionsTags[pool.typeID].property).map(simpleFormat)}
                              keys={Object.keys(systemOptionsTags[pool.typeID].property)}/>);
    fields.push(<LayoutSelect editMode={this.props.edit}
                              label={formatMessage({id: 'general.filter.media'})}
                              dataKey='filterMedia'
                              key='filterMedia'
                              value={pool.filterMedia}
                              dataChanged={this.props.dataChanged}
                              options={Object.values(filters).map(simpleFormat)}
                              keys={Object.keys(filters)}
                              condition={pool.typeID !== systemTypes.spa}/>);
    fields.push(<LayoutCheckbox editMode={this.props.edit}
                                label={formatMessage({id: 'system.details.vacuum.waste'})}
                                dataKey='cannotVacToWaste'
                                key='cannotVacToWaste'
                                condition={pool.typeID !== systemTypes.spa}
                                disabled={pool.typeID === systemTypes.swimspa}
                                value={pool.cannotVacToWaste}
                                dataChanged={this.props.dataChanged}/>);

    let columns = balanceColumns(fields, 3);

    let typeOptions = [
      formatMessage({id: 'general.pool'}),
      formatMessage({id: 'general.spa'}),
      formatMessage({id: 'general.swim.spa'}),
    ];

    let typeKeys = [systemTypes.pool, systemTypes.spa, systemTypes.swimspa];

    if (pool.id) { // Existing
      if (pool.typeID === systemTypes.pool) {
        typeOptions = [
          formatMessage({id: 'general.pool'}),
          formatMessage({id: 'general.swim.spa'}),
        ];
        typeKeys = [systemTypes.pool, systemTypes.swimspa];
      } else if (pool.typeID === systemTypes.spa) {
        typeOptions = [
          formatMessage({id: 'general.spa'}),
          formatMessage({id: 'general.swim.spa'}),
        ];
        typeKeys = [systemTypes.spa, systemTypes.swimspa];
      }
    }

    let maintenanceProgram = dealer && dealer.nameMaintenanceProgram ?
      dealer.nameMaintenanceProgram :
      formatMessage({id: 'preferences.scheduled.maintenance'});

    let output =
      <div className='system-details-static container'
           style={{display: this.props.condition ? 'block' : 'none'}}>
        {this.renderSystemName(pool)}
        <h2 className='with-keyline'><FormattedMessage id="general.properties"/></h2>
        <div className='row'>
          {this.props.edit ?
            <div className='col-xs-4'>
              <div className='form-group'>
                <label><FormattedMessage id="general.system.type"/></label>
                <ToolTip position={tooltipPositions.topRight}
                         iconOnly
                         title={formatMessage({id: 'system.details.switch.system'})}/>
                <EditableSelectField
                  editMode={this.props.edit}
                  value={pool.typeID}
                  dataKey='typeID'
                  options={typeOptions}
                  onChange={this.notifyChangeSystemType}
                  keys={typeKeys}/>
              </div>
            </div> :
            <div className='col-xs-4'>
              <div className='form-group'>
                <label><FormattedMessage id="general.system.type"/></label>
                <ToolTip position={tooltipPositions.topRight}
                         iconOnly
                         title={formatMessage({id: 'system.details.switch.system'})}/>
                <EditableSelectField onChange={this.props.dataChanged}
                                     editMode={false}
                                     value={pool.typeID}
                                     dataKey='typeID'
                                     options={['general.pool', 'general.spa', 'general.swim.spa'].map(id=>formatMessage({id}))}
                                     keys={[systemTypes.pool, systemTypes.spa, systemTypes.swimspa]}/>
              </div>
            </div>}
          <div className='col-xs-4'>
            <LayoutCheckbox editMode={this.props.edit}
                            label={formatMessage({id: 'system.details.installed.by'}, {name: dealer.name})}
                            dataKey='installedByDealer'
                            key='installedByDealer'
                            value={pool.installedByDealer}
                            dataChanged={this.props.dataChanged}/>
          </div>
          <div className='col-xs-4 maintenance-program'>
            <LayoutCheckbox dataChanged={this.props.dataChanged}
                            dataKey='nameMaintenanceProgram'
                            key='nameMaintenanceProgram'
                            label={maintenanceProgram}
                            value={pool.isInMaintenance}
                            editMode={this.props.edit}/>
            <ToolTip position={tooltipPositions.topRight}
                     iconOnly
                     title={formatMessage({id: 'system.details.maintenance.tooltip'})}/>
          </div>
        </div>

        {columns}
        {this.renderWaterFeatures(pool)}
        {this.renderSupplemental(pool)}

        <p className='col-xs-12'>
          <button onClick={this.props.toggleSystemDetails}
                  disabled
                  title={formatMessage({id: 'general.coming.soon'})}
                  className='btn btn-primary'
                  id='toggle-system-details'>
            {this.props.detailsExpanded ?
              <div>
                <i className='bl bl-minus'/> &nbsp; <FormattedMessage id="system.details.fewer.systems"/>
              </div> :
              <div>
                <i className='bl bl-add'/> &nbsp; <FormattedMessage id="system.details.more.systems"/>
              </div>}
          </button>
        </p>
        {details}
      </div>;

    return output;
  }
}

export default injectIntl(DetailsSection);
